import type { ClienteApi } from '../cliente';
import { claveIdempotencia, consulta } from '../comun';
import type { components as Entradas } from '../tipos/eventos-entradas';
import { operacionesMisEntradas } from './misEntradas';
import { operacionesWebPublicaCliente } from './webPublicaCliente';

/**
 * Operaciones de la compra pública de entradas (10.13, tarea 54; G901, ADR-0227), con su `operationId` y los tipos generados
 * de `eventos-entradas.yaml`. Van con el cliente de la API de la zona del cliente y la **clave pública del negocio**
 * (`X-Api-Key`): la de su web (`getPublicEntry`, `/comprar-entradas/{slug}`) o la del widget (`getPublicWidget`,
 * `/comprar-entradas?clave=…&marco=widget`). Crear el pedido lleva además el token de cliente (tras identificarse, 2.9), el
 * antibot (`X-Captcha-Token`, G76) e `Idempotency-Key`. El importe lo calcula siempre la API (`quoteTicketOrder`).
 */
type Esquema<N extends keyof Entradas['schemas']> = Entradas['schemas'][N];

export type EventoPublico = Esquema<'PublicEvent'>;
export type TipoPublico = EventoPublico['ticket_types'][number];
export type ListaEventosPublicos = Esquema<'PublicEventList'>;
export type PeticionPresupuesto = Esquema<'OrderQuoteRequest'>;
export type PresupuestoEntradas = Esquema<'OrderQuote'>;
export type PedidoEnLinea = Esquema<'OnlineOrderCreate'>;
export type PedidoEnLineaCreado = Esquema<'OrderCreated'>;
export type EstadoPedidoEnLinea = Esquema<'OnlineOrderStatus'>;
export type EntradaDelPedido = Esquema<'Ticket'>;

const negocio = (businessId: string, resto: string): `/v1/${string}` => `/v1/businesses/${encodeURIComponent(businessId)}${resto}`;
const llave = (clave: string): Record<string, string> => ({ 'X-Api-Key': clave });

export function operacionesCompraEntradas(api: ClienteApi) {
    return {
        // ---- Negocio y marca (G885, G869) ----
        ...operacionesWebPublicaCliente(api),
        /** listPublicEvents: la cartelera (publicados y públicos), con el precio desde. */
        listPublicEvents: (businessId: string, clave: string) =>
            api.pedir<ListaEventosPublicos>('GET', negocio(businessId, `/public-events${consulta({ limit: 24 })}`), { sinSesion: true, cabeceras: llave(clave) }),
        /** getPublicEvent: el evento con sus tipos de entrada (`on_sale_now`, `available`), la venta y la fecha cambiada. */
        getPublicEvent: (businessId: string, clave: string, eventId: string) =>
            api.pedir<EventoPublico>('GET', negocio(businessId, `/public-events/${encodeURIComponent(eventId)}`), { sinSesion: true, cabeceras: llave(clave) }),
        /** quoteTicketOrder con la clave pública: subtotal, gastos de gestión y total (los da la API). */
        quoteTicketOrderPublic: (businessId: string, clave: string, datos: PeticionPresupuesto) =>
            api.pedir<PresupuestoEntradas>('POST', negocio(businessId, '/ticket-order-quotes'), { cuerpo: datos, sinSesion: true, cabeceras: llave(clave), idempotencia: claveIdempotencia() }),
        /**
         * createOnlineTicketOrder: con el token de cliente, el antibot y `expected_total` (si cambió, `409 conflict`); sin
         * plazas, `409 capacity_unavailable`. Con importe, `next_action.url` (la página de pago, 30 min, G39).
         */
        createOnlineTicketOrder: (businessId: string, clave: string, datos: PedidoEnLinea, captcha: string | null, idem = claveIdempotencia()) =>
            api.pedir<PedidoEnLineaCreado>('POST', negocio(businessId, '/online-ticket-orders'), {
                cuerpo: datos,
                idempotencia: idem,
                cabeceras: { ...llave(clave), ...(captcha === null ? {} : { 'X-Captcha-Token': captcha }) },
            }),
        /** getOnlineTicketOrderStatus: a la vuelta del pago, el estado del pedido con el `order_token` del alta (sin datos personales). */
        getOnlineTicketOrderStatus: (businessId: string, clave: string, orderId: string, orderToken: string) =>
            api.pedir<EstadoPedidoEnLinea>('GET', negocio(businessId, `/online-ticket-orders/${encodeURIComponent(orderId)}${consulta({ order_token: orderToken })}`), { sinSesion: true, cabeceras: llave(clave) }),
        /** getMyTicketOrder (token de cliente): las entradas del pedido pagado, con su `pass_id`, para «hecha». */
        getMyTicketOrder: operacionesMisEntradas(api).getMyTicketOrder,
    };
}

export type OperacionesCompraEntradas = ReturnType<typeof operacionesCompraEntradas>;
